"use client";

import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { discussionsApi } from "@/lib/api/services/discussions.api";
import { eventsApi } from "@/lib/api/services/events.api";
import { learningApi } from "@/lib/api/services/learning.api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { MessageSquare, Plus, Clock, Calendar, BookOpen, TrendingUp, Users } from "lucide-react";
import Link from "next/link";
import { Pagination } from "@/components/shared/pagination";
import { useRequireAuth } from "@/hooks/use-auth-gate";

interface Discussion {
  id: string;
  title: string;
  description: string;
  category: string;
  tags: string[];
  author: string;
  createdAt: string;
  replyCount: number;
  upvoteCount: number;
}

export default function DiscussionsListPage() {
  const [page, setPage] = useState(1);
  const { requireAuth, AuthModal } = useRequireAuth("/discussions/create");

  const { data, isLoading } = useQuery({
    queryKey: ["discussions", page],
    queryFn: async () => {
      const [discData, eventData, courseData] = await Promise.all([
        discussionsApi.list({ page, perPage: 20 }),
        eventsApi.list({ page: 1, perPage: 3, status: "upcoming" }),
        learningApi.listCourses({ page: 1, perPage: 3 }),
      ]);
      return {
        discussions: ((discData as unknown as { data?: Discussion[] }).data ?? []) as Discussion[],
        meta: (discData as { meta?: { total: number; totalPages: number } }).meta ?? { total: 0, totalPages: 1 },
        relatedEvents: ((eventData as unknown as { data?: Array<{ id: string; title: string; date: string }> }).data ?? []),
        relatedCourses: ((courseData as unknown as { data?: Array<{ id: string; title: string; duration: string }> }).data ?? []),
      };
    },
  });

  const discussions = data?.discussions ?? [];
  const meta = data?.meta ?? { total: 0, totalPages: 1 };
  const relatedEvents = data?.relatedEvents ?? [];
  const relatedCourses = data?.relatedCourses ?? [];

  const handleNewDiscussion = (e: React.MouseEvent) => {
    if (!requireAuth()) {
      e.preventDefault();
    }
  };

  return (
    <div className="mx-auto max-w-3xl px-4 py-8">
      <div className="flex items-center justify-between mb-6">
        <div className="space-y-1">
          <h1 className="font-heading text-xl font-semibold tracking-tight text-foreground">Discussions</h1>
          <p className="text-sm text-muted-foreground leading-relaxed">Ask questions, share insights, and connect with the community.</p>
        </div>
        <Button asChild size="sm">
          <Link href="/discussions/create" onClick={handleNewDiscussion}><Plus className="mr-2 h-4 w-4" /> New Discussion</Link>
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="space-y-3 p-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="h-8 w-8 rounded-full bg-muted/10 animate-pulse shrink-0" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 w-3/4 bg-muted/10 rounded animate-pulse" />
                    <div className="h-3 w-1/2 bg-muted/10 rounded animate-pulse" />
                  </div>
                </div>
              ))}
            </div>
          ) : discussions.length === 0 ? (
            <div className="py-16 text-center">
              <MessageSquare className="mx-auto h-10 w-10 text-muted-foreground/30" />
              <h3 className="mt-4 font-heading text-base font-semibold text-foreground">No discussions yet</h3>
              <p className="mt-1 text-sm text-muted-foreground">Start a conversation with the community.</p>
              <Button className="mt-4" size="sm" asChild>
                <Link href="/discussions/create" onClick={handleNewDiscussion}>Create Discussion</Link>
              </Button>
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              {discussions.map((discussion) => (
                <Link key={discussion.id} href={`/discussions/${discussion.id}`}>
                  <Card className="group h-full hover:border-neutral-300 transition-all duration-150">
                    <CardContent className="p-5">
                      <div className="flex items-center gap-2 mb-3">
                        <Badge variant="secondary" size="sm">{discussion.category}</Badge>
                        <span className="text-xs text-muted-foreground">{discussion.createdAt}</span>
                      </div>
                      <h3 className="text-sm font-heading font-semibold text-foreground group-hover:text-primary transition-colors line-clamp-2 mb-2">
                        {discussion.title}
                      </h3>
                      <p className="text-xs text-muted-foreground leading-relaxed line-clamp-2 mb-4">{discussion.description}</p>
                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1"><Users className="h-3 w-3" /> {discussion.author}</span>
                        <span className="flex items-center gap-1"><MessageSquare className="h-3 w-3" /> {discussion.replyCount} replies</span>
                        <span className="flex items-center gap-1"><TrendingUp className="h-3 w-3" /> {discussion.upvoteCount}</span>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {!isLoading && (
        <Pagination page={page} totalPages={meta.totalPages} total={meta.total} onPageChange={setPage} />
      )}

      {!isLoading && (relatedEvents.length > 0 || relatedCourses.length > 0) && (
        <div className="mt-8 space-y-4">
          <h2 className="font-heading text-sm font-semibold text-foreground">Related Content</h2>
          {relatedEvents.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2 font-medium"><Calendar className="h-4 w-4 text-muted-foreground" /> Upcoming Events</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y divide-border/60">
                  {relatedEvents.map((e) => (
                    <div key={e.id} className="flex items-center gap-3 p-3 transition-colors hover:bg-muted/10">
                      <Calendar className="h-4 w-4 text-muted-foreground shrink-0" />
                      <Link href={`/events/${e.id}`} className="text-sm font-medium text-foreground hover:text-primary transition-colors min-w-0 truncate">{e.title}</Link>
                      <span className="text-xs text-muted-foreground shrink-0 ml-auto">{e.date}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
          {relatedCourses.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2 font-medium"><BookOpen className="h-4 w-4 text-muted-foreground" /> Recommended Courses</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y divide-border/60">
                  {relatedCourses.map((c) => (
                    <div key={c.id} className="flex items-center gap-3 p-3 transition-colors hover:bg-muted/10">
                      <BookOpen className="h-4 w-4 text-muted-foreground shrink-0" />
                      <Link href={`/learning/courses/${c.id}`} className="text-sm font-medium text-foreground hover:text-primary transition-colors min-w-0 truncate">{c.title}</Link>
                      <span className="text-xs text-muted-foreground shrink-0 ml-auto">{c.duration}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}
      <AuthModal />
    </div>
  );
}